import React, { useEffect, useMemo, useState } from 'react'
import { renderDateTime } from '../../../utils/dateFormatter'
import { getFileSize } from '../../../utils/helpers'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Joi from 'joi'
import { joiResolver } from '@hookform/resolvers/joi'
import { useForm } from 'react-hook-form'
import { Col, Form, FormGroup, ModalBody, Row } from 'reactstrap'
import { Extension, maxOptions, Version } from '../types'
import './DataSetsDisplay.css'
import '../../Data/Results/DataViewCell.css'
import '../../Data/Results/ResultsGridColData.css'
import { ICSModal, TanStackDatagrid, TanStackDatagridWithExport } from '../../../components'
import ICSTextInput from '../../../components/ICSTextInput/ICSTextInput'
import ICSButton from '../../../components/ICS/Button/ICSButton'
import ComponentBoxFooter from '../../../components/ComponentBox/ComponentBoxFooter/ComponentBoxFooter'
import ICSTextAreaInput from '../../../components/ICSTextAreaInput/ICSTextAreaInput'
import ICSErrorCard from '../../../components/ICSErrorCard/ICSErrorCard'
import { createDataset, setEntityError } from '../../../redux/slices'
import { DisplayErrorState, AppDispatch } from '../../../services/lib'
import { hasInvalidField, invalidFieldsMessage } from '../../../utils/helpers'
import { DefaultState } from '../../../types'
import { RequestErrorResponse } from '../../../services/lib/types'
import { generateDatasetFilters } from './genDatasetFilters'
import {
  TanStackDatagridColumn,
  TanStackDatagridPagination
} from '../../../components/TanStackDataGrid/TanStackDatagrid'

interface DataSetDisplayRow {
  id?: string | number
  startDate?: string
  endDate?: string
  vehicle?: {
    displayName?: string
    name?: string
  }
  displayName?: string
  script?: {
    name?: string
  }
  versions: Version[]
}

const defaultJoiSchema = {
  name: Joi.string().required().min(3).max(30).messages({
    'string.empty': `Required field`,
    'string.min': `Must be at lease {#limit} in length`
  }),
  description: Joi.string().min(0).max(255).allow('')
}

const schema = Joi.object(defaultJoiSchema)

function DataSetsDisplay({
  resultJson,
  setResultJson,
  dynamic,
  extensions,
  resultJsonErrors,
  filters
}: {
  resultJson
  setResultJson
  dynamic: boolean
  extensions
  resultJsonErrors: DisplayErrorState
  filters
}) {
  let extensionsToShow = extensions.filter((e: Extension) => e.selected)
  if (extensionsToShow.length === 0) extensionsToShow = extensions
  const [selectedTotal, setSelectedTotal] = useState<{
    count: number
    size: number
    selectedFiles: Version[] | never[]
  }>({
    count: 0,
    size: 0,
    selectedFiles: []
  })

  const dispatch: AppDispatch = useDispatch()
  const navigate = useNavigate()
  const datasetErrors = useSelector(
    (state: DefaultState) => state.datasets.error
  )
  const [hidden, setHidden] = useState<boolean>(false)
  const [pagination, setPagination] = useState<TanStackDatagridPagination>({
    pageIndex: 0,
    pageSize: maxOptions[0]
  })

  const {
    formState: { errors, isSubmitted, isValid },
    handleSubmit,
    control,
    setError
  } = useForm({
    mode: 'onChange',
    resolver: joiResolver(schema)
  })

  useEffect(() => {
    if (datasetErrors) {
      datasetErrors.fields.forEach(async (errorObj) =>
        setError(errorObj.field, {
          type: 'manual',
          message: errorObj.message
        })
      )
    }
  }, [datasetErrors, setError])

  const toggleCreateModal = () => {
    if (hidden)
      dispatch(
        setEntityError({
          sliceName: 'datasets',
          entityName: 'Dataset',
          error: null
        })
      )
    setHidden(hidden ? false : true)
  }

  function updateTotal() {
    let count = 0
    let sizeTotal = 0
    const selectedFiles: Version[] = []
    resultJson.forEach((result) => {
      result.versions.forEach((version: Version) => {
        if (version.selected) {
          count++
          sizeTotal += version.size
          selectedFiles.push(version)
        }
      })
    })
    setSelectedTotal({
      count,
      size: sizeTotal,
      selectedFiles
    })
  }

  function updateSelectedForAll(extension) {
    extension.active = !extension.active
    resultJson.forEach((result) => {
      result.versions.forEach((version: Version) => {
        if (version.extension === extension.name) {
          version.selected = extension.active
        }
      })
    })
    setResultJson([...resultJson])
    updateTotal()
  }

  function updateSelectedForOne(item) {
    item.selected = !item.selected
    setResultJson([...resultJson])
    updateTotal()
  }

  function renderDataType(versions: Version[]) {
    return <div className="dataset-display results-data-type-cell flexxer">
        {versions.map((item, i: number) => {
          return (
            <button
              className={`rgvc-icon-btn data-type-button ${
                item.selected ? 'active' : ''
              }`}
              onClick={() => updateSelectedForOne(item)}
              key={`${item.id}-${i}`}
            >
              <div className="data-type-extension">
                {item.extension.toUpperCase()}
              </div>
              <div className="data-type-size">{getFileSize(item.size)}</div>
            </button>
          )
        })}
    </ div>
  }

  const onSubmit = async (formValues) => {
    const { description, name } = formValues

    const selectedFiles = selectedTotal.selectedFiles.filter((file) => {
      return file.selected
    })

    const uploadFileIds = selectedFiles.map((file) => {
      return file.id
    })

    const createdDataset = await dispatch(
      createDataset({
        name: name,
        description: description,
        type: dynamic ? 'dynamic' : 'static',
        filters: generateDatasetFilters(filters),
        upload_file_version_id: uploadFileIds
      })
    )

    if (createdDataset.type.split('/').pop() === 'fulfilled') {
      navigate('/ui2/datasets')
    } else {
      const verifyFields = await hasInvalidField(
        Object.keys(defaultJoiSchema),
        (createdDataset.payload as RequestErrorResponse).fields.map(
          (fieldObj: { field: string; message: string }) => fieldObj.field
        )
      )

      if (verifyFields.isInvalid) {
        dispatch(
          setEntityError({
            sliceName: 'datasets',
            entityName: 'Dataset',
            error: {
              code: 'error',
              message: invalidFieldsMessage,
              fields: verifyFields.fields
            }
          })
        )
      }
    }
  }

  const createDataSetModal = (
    <ICSModal
      visible={hidden}
      onHiding={() => {
        setHidden(false)
        dispatch(
          setEntityError({
            sliceName: 'datasets',
            entityName: 'Dataset',
            error: null
          })
        )
      }}
      dragEnabled={false}
      closeOnOutsideClick={true}
      title="Create Data Set"
    >
      <ModalBody>
        <Row>
          <Col xs={{ size: 12 }}>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <FormGroup>
                <Row>
                  <Col>
                    {'Creating data sets with selected files count: ' +
                      selectedTotal.count}
                    <br />
                    {'Total size: ' + getFileSize(selectedTotal.size)}
                  </Col>
                </Row>
                <Row>
                  <Col>
                    {datasetErrors?.code === 'error' ? (
                      <ICSErrorCard
                        code={datasetErrors.code}
                        message={datasetErrors.message}
                        fields={datasetErrors.fields}
                      />
                    ) : (
                      <>
                        <ICSTextInput
                          name="name"
                          control={control}
                          errors={errors}
                          isSubmitted={isSubmitted}
                        />
                        <ICSTextAreaInput
                          name="description"
                          control={control}
                          errors={errors}
                          isSubmitted={isSubmitted}
                        />
                      </>
                    )}
                  </Col>
                </Row>
                <ComponentBoxFooter>
                  <ICSButton
                    id="formSubmit"
                    width={140}
                    useSubmitBehavior={true}
                    type="success"
                    disabled={!isValid}
                  >
                    Save
                  </ICSButton>
                </ComponentBoxFooter>
              </FormGroup>
            </Form>
          </Col>
        </Row>
      </ModalBody>
    </ICSModal>
  )

  const data = useMemo<DataSetDisplayRow[]>(() => resultJson ?? [], [resultJson])

  const columns = useMemo<TanStackDatagridColumn<DataSetDisplayRow>[]>(() => [
    {
      id: 'startDate',
      header: 'Start',
      accessor: (result) => result.startDate || '',
      cell: (result) => renderDateTime({ value: result.startDate || '' }) || '',
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    },
    {
      id: 'endDate',
      header: 'End',
      accessor: (result) => result.endDate || '',
      cell: (result) => renderDateTime({ value: result.endDate || '' }) || '',
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    },
    {
      id: 'vehicle',
      header: 'Vehicle',
      accessor: (result) => result.vehicle?.displayName || result.vehicle?.name || '',
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    },
    {
      id: 'displayName',
      header: 'Name',
      accessor: (result) => result.displayName || '',
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    },
    {
      id: 'scriptName',
      header: 'Script',
      accessor: (result) => result.script?.name || '',
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    },
    {
      id: 'versions',
      header: 'Data Type',
      accessor: (result) => result.versions,
      className: 'p-0 results-data-type-cell',
      cell: (result) => renderDataType(result.versions || []),
      resizable: true,
      hideable: true
    }
  ], [resultJson])

  if (dynamic) {
    return (
      <div className="alert alert-info">
        <div>All query results will be used for dynamic dataset</div>
      </div>
    )
  }

  return (
    <>
      {resultJsonErrors?.message ? (
        <ICSErrorCard
          code={resultJsonErrors.code}
          message={resultJsonErrors.message}
          fields={resultJsonErrors.fields}
        />
      ) : (
        <>
          {createDataSetModal}
          <div className="well well-sm clearfix">
            <div className="btn-group pull-right">
              {extensionsToShow.length > 0 && (
                <label className="select-all">Select All</label>
              )}
              {extensionsToShow.map((extension, i: number) => (
                <button
                  key={`${extension}-${i}`}
                  className={`extension-button ${
                    extension.active ? 'active' : ''
                  }`}
                  onClick={() => updateSelectedForAll(extension)}
                >
                  {extension.name}
                </button>
              ))}
            </div>
            <div className="pull-right">
              <button
                className="glyphicons glyphicons-package"
                disabled={selectedTotal.count === 0}
                onClick={toggleCreateModal}
              >
                Create Static Data Set
              </button>
              <button className="glyphicons glyphicons-check" disabled>
                {selectedTotal.count} files ({getFileSize(selectedTotal.size)})
                selected
              </button>
              <ul uib-dropdown-menu="" className="dropdown-menu">
                <li>
                  <a>
                    <span className="glyphicons glyphicons-compressed"></span>{' '}
                    Create Download
                  </a>
                </li>
                <li role="separator" className="divider"></li>

                <li>
                  <a>
                    <span className="glyphicons glyphicons-restart"></span>{' '}
                    Export Selections
                  </a>
                </li>
                <li role="separator" className="divider"></li>
                <li>
                  <a>
                    <span className="glyphicons glyphicons-remove"></span> Clear
                    Selections
                  </a>
                </li>
                <li role="separator" className="divider"></li>
                <li>
                  <a>
                    <span className="glyphicons glyphicons-bin"></span> Delete
                    Selections
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="dataset-display">
            <TanStackDatagridWithExport
              data={data}
              columns={columns}
              getRowId={(result) =>
                String(
                  result.id ||
                    [
                      result.displayName || 'dataset',
                      result.startDate || 'start',
                      result.endDate || 'end'
                    ].join('-')
                )
              }
              enablePagination
              pagination={pagination}
              onPaginationChange={setPagination}
              pageSizeOptions={maxOptions}
            />
          </div>
        </>
      )}
    </>
  )
}

export default DataSetsDisplay
